import { CLabel, CTextarea } from "@coreui/react"
import { NoDataAndWentWrongHandler } from "components/common/noData"
// import AccountProvider from "components/common/Context/AccountContext"
import { Table } from "components/common/table"
import {
  getRequestRedirectionLink,
  prodSnowHost,
  ritmNotificationHeaders
} from "constants/Constants"
import { getRITMDetails } from "extensions/usoc/api/service"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { useExtension, useUserAuth } from "utils"
import { isAnyAPIFailed } from "utils/nodata"

import { CMSButton } from "../button"
import { OptionDropdown } from "../dropdown"
import CMSTypography, { fontSizes } from "../typography/CMSTypography"

async function fetchRITMDetails(auth, extension, notifications) {
  const host = extension?.config?.env?.clientApiGateway?.URL

  const promises = []
  for (const notification of notifications) {
    promises.push(
      getRITMDetails(
        { auth, host },
        notification.project_id,
        `ritm_number=${notification?.ticket_number}`
      ).catch((err) => {
        throw err
      })
    )
  }
  const results = await Promise.all(promises)
  return results
}

const stateOptions = [
  { text: "Not Requested", key: "not requested" },
  { text: "Requested", key: "requested" },
  { text: "Approved", key: "approved" },
  { text: "Rejected", key: "rejected" },
  { text: "Cancelled", key: "cancelled" },
  { text: "Not Required", key: "not_required" },
  { text: "More info required", key: "more_info_required" },
  { text: "Duplicate", key: "duplicate" }
]

/**
 * Component render Notification table for Change Request under Notification Drawer
 *
 * @example
 * const ritmNotifications = [
 * {
 *   sys_id: "8509380e1be270101683c913604bcb54",
 *   project_id: "PROJ0037154",
 *   ticket_number: "CHG0253454"
 * }]
 * <NotificationRITMTable
 *   notifications={ritmNotifications}
 *   onApproveClick={onApproveClick}
 * />
 */
export default function NotificationRITMTable({
  notifications,
  onApproveClick
}) {
  const auth = useUserAuth()

  // const { projectBonding } = useContext(AccountProvider)
  const [ritmData, setRitmData] = useState([])
  const [expandedRow, setExpandedRow] = useState("")
  const [status, setStatus] = useState("requested")
  const [comments, setComments] = useState("")

  const extension = useExtension()
  const userRole = useSelector((state) => state.user.role)

  // Making api call for getting data about RITM notifications
  const { data: RITMResult } = useSWR(
    () =>
      Array.isArray(notifications) && notifications.length
        ? notifications?.map(
          (notification) =>
            `/projects/${notification.project_id}/get_ritm_details?ritm_number=${notification?.ticket_number}`
        )
        : null,
    () => fetchRITMDetails(auth, extension, notifications),
    { suspense: true }
  )

  useEffect(() => {
    if (RITMResult) {
      setRitmData(
        RITMResult.reduce((acc, element) => {
          if (element.data[0]?.sys_id) {
            acc.push(element.data[0])
          }
          return acc
        }, [])
      )
    }
  }, [RITMResult])

  // To handle the expansion of particular row(either we want want toexpand row or close the expanded row)
  function toggleDetails(id) {
    setExpandedRow((prevExpRow) => (prevExpRow === id ? "" : id))
    setComments("")
    setStatus("requested")
  }

  const data = ritmData.map((data) => {
    return {
      _key: data.sys_id,
      _onClick: () => {
        toggleDetails(data.sys_id)
      },
      _expandedRow:
        expandedRow === data.sys_id ? (
          <td colSpan={12} className="noti-extra-details">
            <div className="d-flex flex-column mx-3 mb-3">
              <div className="noti-feedback-comment">
                <div className="d-flex flex-row h-100">
                  <CLabel className="font-weight-bold mr-2">COMMENTS: </CLabel>
                  <CTextarea
                    placeholder="Pls write your Comments here!"
                    className="text-area w-100 mr-5"
                    onChange={(e) => setComments(e.target.value)}
                  />
                </div>
                <div className="d-flex flex-column-reverse">
                  <div className="d-flex flex-row pt-3">
                    <CLabel className="font-weight-bold pt-2 pr-2">
                      State:{" "}
                    </CLabel>
                    <OptionDropdown
                      items={stateOptions}
                      value={status}
                      onChange={setStatus}
                      className="pr-3"
                      buttonClassName="border"
                    />
                    <CMSButton
                      type="primary"
                      onClick={() =>
                        onApproveClick(status, comments, data.sys_id, "ritm")
                      }
                    >
                      Submit
                    </CMSButton>
                  </div>
                </div>
              </div>
            </div>
          </td>
        ) : null,
      ritm_number: {
        text: data.ritm_number,
        ele: (
          <CMSTypography
            fontSize={fontSizes.small}
            clickable
            href={
              getRequestRedirectionLink(userRole, prodSnowHost) + data?.sys_id
            }
            onClick={(event) => event?.stopPropagation()}
          >
            {data.ritm_number}
          </CMSTypography>
        )
      },
      request_number: { text: data.request_number },
      short_description: { text: data.short_description },
      opened_at: { text: data.opened_at },
      priority: { text: data.priority },
      approval: { text: data.approval }
    }
  })
  return (
    <NoDataAndWentWrongHandler wentWrongCondition={isAnyAPIFailed(RITMResult)}>
      <Table
        headers={ritmNotificationHeaders}
        data={data}
        disableSearch
        dataTestId="ritm-notifications"
        disableExport
        disablePagination
      />
    </NoDataAndWentWrongHandler>
  )
}

NotificationRITMTable.propTypes = {
  notifications: PropTypes.array,
  onApproveClick: PropTypes.func
}
